import { FC } from 'react';
import { FormControl as MuiFormControl, FormControlLabel, styled } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { Radio } from '../Radio/Radio';
import { RadioGroup } from '../RadioGroup';
import { IFormControlProps, IRadioOption } from './types';
import FormHelperText from '@mui/material/FormHelperText';

const StyledFormControl = styled(MuiFormControl, {
  shouldForwardProp: prop => prop !== 'isWide',
})<any>(({ isWide }) => ({
  '&.MuiFormControl-root': {
    width: isWide ? '100%' : '60%',
  },
}));

const StyledRadioGroup = styled(RadioGroup, {
  shouldForwardProp: prop => prop !== 'columnsAmount',
})<any>(({ theme, columnsAmount }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columnsAmount}, 1fr)`,
  gridGap: '0.8rem',
  gridAutoRows: '1fr',

  [theme.breakpoints.down('md')]: {
    gridGap: '0.45rem',
  },

  '&.Mui-checked': {
    backgroundColor: theme.colors.grass,
  },
}));

const StyledControlLabel = styled(FormControlLabel, {
  shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isError' && prop !== 'noPadding',
})<any>(({ theme, isActive, isError, noPadding }) => ({
  border: `1px solid ${isError ? theme.palette.error.light : isActive ? theme.colors.grass : theme.colors.dust}`,
  backgroundColor: isActive ? theme.colors.grass : theme.colors.white,
  borderRadius: '0.5rem',
  display: 'flex',
  alignItems: 'center',
  padding: noPadding ? 0 : '0.94rem 1rem',
  minWidth: '5rem',
  justifyContent: 'center',
  margin: 0,

  [theme.breakpoints.down('md')]: {
    minWidth: '1rem',
  },

  '& > .MuiTypography-root': {
    color: `${isActive ? theme.colors.white : theme.colors.ash}`,
    fontWeight: 900,
  },
}));

const StyledRadio = styled(Radio)(() => ({
  '&.MuiRadio-root': {
    width: '1px',
    height: '1px',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    position: 'absolute',
  },
}));

/** MUI RadioGroup passes `event.target.value` as a string; null option maps to "" in the DOM. */
function matchRadioOptionValue(
  incoming: string | number | boolean | null | undefined,
  radioOptions: IRadioOption[],
): IRadioOption | undefined {
  return radioOptions.find(o => {
    if (o.value === null) {
      return incoming === '' || incoming === null || incoming === undefined || incoming === 'null';
    }
    return String(o.value) === String(incoming);
  });
}

export const FormControl: FC<IFormControlProps> = ({
  radioOptions,
  name,
  radioGroupProps,
  rules,
  onChangeEffect,
  isWide = false,
  className,
  noPadding,
  columnsAmount,
  disabled,
}) => {
  const intl = useIntl();
  const { control } = useFormContext();

  return (
    <StyledFormControl className={className} isWide={isWide}>
      <Controller
        rules={rules}
        control={control}
        name={name}
        render={({ field: { ref, ...field }, fieldState: { error } }) => {
          const hasNullOption = radioOptions.some(o => o.value === null);
          const normalizedGroupValue =
            hasNullOption && (field.value === '' || field.value === undefined) ? null : field.value;

          return (
            <>
              <StyledRadioGroup
                {...field}
                {...radioGroupProps}
                value={normalizedGroupValue}
                columnsAmount={columnsAmount ?? radioOptions.length}
                onChange={(_event: React.SyntheticEvent, value: string | number | boolean | null) => {
                  const selected = matchRadioOptionValue(value, radioOptions);
                  field.onChange(selected ? selected.value : value);
                  onChangeEffect?.(_event);
                }}
              >
                {radioOptions.map(({ value, labelKey }: IRadioOption) => {
                  const isActive =
                    value === null
                      ? field.value === null || field.value === '' || field.value === undefined
                      : String(field.value) === String(value);

                  return (
                    <StyledControlLabel
                      key={`${name}-${value}`}
                      data-testid={`radio-${name}-${value}`}
                      isActive={isActive}
                      value={value}
                      isError={!!error}
                      disabled={disabled}
                      control={<StyledRadio />}
                      label={intl.formatMessage({ id: labelKey })}
                      inputRef={ref}
                      noPadding={noPadding}
                    />
                  );
                })}
              </StyledRadioGroup>
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          );
        }}
      />
    </StyledFormControl>
  );
};
